<template>
    <div
        v-if="data?.length !== 0"
        class="component-comments-content"
    >
        <div class="component-comments-content__table">
            <div ref="tableRef" />
        </div>
    </div>
    <EmptyStatePanelContent v-else />
</template>

<script setup lang="ts">
import {  computed, ref, watch } from 'vue'
import { PebbleTable } from '@pebble/tables'
import { CellComponent, ColumnDefinition, Options } from 'tabulator-tables'
import { format } from 'date-fns'
import { DATE_FORMAT } from '@/utils/date-utils'
import { useI18n } from 'vue-i18n'
import { createElement, Paperclip, PencilLine, Trash2 } from 'lucide'
import { GeneralCommentDto } from '@/api/generated/HCA'
import { useGlobalConfirmationModal } from '@/composables/useGlobalConfirmationModal'
import EmptyStatePanelContent from '@/components/states/EmptyStatePanelContent.vue'
import { usePermissions } from '@/composables/usePermissions'
import { useUserStore } from '@/store/userStore'
import { DeleteCommentPayload } from '@/types/comments'
import { ComponentCommentOpenPayload } from './component-comment/useComponentCommentModal'
import { useDownloadGeneralCommentsAttachmentMutation } from '@/api/mutations/general-comments/useDownloadGeneralCommentsAttachmentMutation'
import { downloadFile } from '@/utils/file-utils'
import { ActionType } from '@/configuration/actions/action-type'

const props = defineProps<{
    data?: Array<GeneralCommentDto> | null,
    generalCommentComponentSid: string,
}>()

const emit = defineEmits<{
    'deleteComment': [payload: DeleteCommentPayload],
    'editComment': [payload: ComponentCommentOpenPayload],
    'fetchNextPage': [],
}>()

const { t, locale } = useI18n()
const triggerGlobalConfirmationModal = useGlobalConfirmationModal()

const { userPermissions } = usePermissions()
const userStore = useUserStore()
const userKid = userStore.userData?.kid

const tableRef = ref<HTMLDivElement | null>(null)
const pebbleTable = ref<PebbleTable | null>(null)

const tableRowData = computed(() => props.data ?? [])

const { mutateAsync: downloadAttachment } = useDownloadGeneralCommentsAttachmentMutation()

const onEditIconClick = (commentDto: ComponentCommentOpenPayload) => {
    emit('editComment', commentDto)
}

const onDeleteIconClick = (commentDto: ComponentCommentOpenPayload) => {
    const commentId = commentDto.id

    if (!commentId) {
        return
    }

    triggerGlobalConfirmationModal({
        headline: t('conditionStatus.content.common.comments.deleteConfirmation.headline'),
        content: t('conditionStatus.content.common.comments.deleteConfirmation.content'),
        confirmButtonText: t('conditionStatus.content.common.comments.deleteConfirmation.headline'),
        onConfirm: async () => {
            emit('deleteComment', {
                commentId,
            })
        },
    })
}

const onDownloadAttachment = async (attachmentId: number, attachmentName: string) => {
    await downloadAttachment({ fileId: attachmentId, generalCommentComponentSid: props.generalCommentComponentSid }).then((res) => {
        downloadFile(res, attachmentName)
    })
}

watch([tableRef, tableRowData, locale], ([newTableRef, newTableRowData]) => {
    if (!newTableRef) {
        return
    }

    const columns: ColumnDefinition[] = [
        {
            title: t('common.date'),
            field: 'createdAt',
            formatter: (cell: CellComponent) => {
                const rowData: GeneralCommentDto = cell.getRow().getData()
                const updatedAt = rowData.updatedAt
                return updatedAt ? format(new Date(updatedAt), DATE_FORMAT) : format(new Date(cell.getValue()), DATE_FORMAT)
            },
        },
        {
            title: t('common.comment'),
            field: 'text',
            formatter: (cell: CellComponent) => {
                const rowData: GeneralCommentDto = cell.getRow().getData()

                const cellDiv = document.createElement('div')
                cellDiv.classList.add('component-comments-text-cell')

                if (rowData.actionType === ActionType.UnmarkedAsNoAction || rowData.actionType === ActionType.MarkedAsNoAction) {
                    const title = t(`dashboard.notifications.actions.${rowData.actionType}`)

                    const titleSpan = document.createElement('span')
                    titleSpan.classList.add('component-comments-text-cell__title')
                    titleSpan.textContent = title
                    cellDiv.appendChild(titleSpan)
                }

                const textDiv = document.createElement('div')
                textDiv.classList.add('component-comments-text-cell__text')
                textDiv.textContent = rowData.text ?? '-'
                cellDiv.appendChild(textDiv)

                const attachment = rowData.attachment
                if (attachment?.id && attachment.name) {
                    const attachmentDiv = document.createElement('div')
                    attachmentDiv.classList.add('component-comments-attachment')
                    attachmentDiv.addEventListener('click', () => onDownloadAttachment(attachment.id as number, attachment.name as string))
                    const attachmentIcon = createElement(Paperclip)
                    attachmentIcon.classList.add('component-comments-attachment__icon')
                    attachmentDiv.appendChild(attachmentIcon)
                    const attachmentNameSpan = document.createElement('span')
                    attachmentNameSpan.textContent = attachment.name
                    attachmentDiv.appendChild(attachmentNameSpan)
                    cellDiv.appendChild(attachmentDiv)
                }

                return cellDiv
            },
            widthGrow: 8,
        },
        {
            title: t('common.kid'),
            field: 'createdBy',
        },
        {
            title: '',
            field: 'createdBy',
            visible: !!userPermissions.value.canEditTransformerComments,
            formatter: (cell: CellComponent) => {
                const rowData: GeneralCommentDto = cell.getRow().getData()
                const createdBy = cell.getValue()

                const cellDiv = document.createElement('div')
                cellDiv.classList.add('component-comments-action-cell')

                if (createdBy === userKid && !rowData.actionType) {
                    const editIcon = createElement(PencilLine)
                    editIcon.addEventListener('click', () => onEditIconClick(cell.getRow().getData()))
                    editIcon.classList.add('component-comments-action-cell__icon')
                    cellDiv.appendChild(editIcon)

                    const deleteIcon = createElement(Trash2)
                    deleteIcon.addEventListener('click', () => onDeleteIconClick(cell.getRow().getData()))
                    deleteIcon.classList.add('component-comments-action-cell__icon')
                    cellDiv.appendChild(deleteIcon)
                }

                return cellDiv
            },
        },
    ]

    const tabulatorOptions: Options = {
        layout: 'fitColumns',
        height: '100%',
        pagination: false,
        reactiveData: true,
        data: newTableRowData,
        columnDefaults: {
            title: '',
            headerSort: false,
            vertAlign: 'middle',
        },
        columns,
    }

    pebbleTable.value?.destroy()
    pebbleTable.value = new PebbleTable(newTableRef, tabulatorOptions)
})
</script>

<style scoped lang="scss">
.component-comments-content {
    display: flex;
    flex-direction: column;
    gap: $spacing-xs;

    &__table {
        border: 1px solid $pbl-border-muted;
    }
}
</style>

<style lang="scss">
.component-comments-action-cell {
    display: flex;
    gap: $spacing-2xs;

    align-items: center;

    &__icon {
        height: 18px;
        width: 18px;

        color: $pbl-primary;

        &:hover {
            color: color-mix(in srgb, $pbl-primary 80%, black);
        }
    }
}

.component-comments-attachment {
    display: flex;
    gap: $spacing-xs;
    align-items: center;
    color: $pbl-primary;

    &:hover {
        color: color-mix(in srgb, $pbl-primary 80%, black);
    }

    &__icon {
        height: 14px;
        width: 14px;
    }
}

.component-comments-text-cell {
    display: flex;
    flex-direction: column;

    gap: $spacing-3xs;
    white-space: pre-wrap;

    &__title {
        font-weight: 500;
    }
}
</style>
